import React from 'react';
import { Mic, ExternalLink, Users, Wrench, Headphones, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { ShareButton } from './ShareButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_SRC = `${API_URL}/api/uploads/files/amped_up_life_logo.png`;
const HOSTS_SRC = `${API_URL}/api/uploads/files/amped_up_hosts_portrait_gritty.png`;

/**
 * THE AMPED UP LIFE — Podcast & Social Restoration Community
 * A Division of MINDESIGN ENTERPRISES & JFRJR.
 *
 * Mounted right after MINDESIGN Radio so the audio family stays together.
 */
export const AmpedUpLife = () => {
  return (
    <section
      id="amped-up-life"
      className="relative py-24 px-6 overflow-hidden"
      data-testid="amped-up-life-section"
    >
      {/* Industrial / amber backdrop — distinct from Radio's deep gunmetal */}
      <div className="absolute inset-0 bg-gradient-to-br from-gunmetal-950 via-[#1a1408] to-gunmetal-950"></div>
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 30% 30%, rgba(251,191,36,0.18) 0%, transparent 60%), radial-gradient(circle at 70% 70%, rgba(59,130,246,0.12) 0%, transparent 60%)',
        }}
      ></div>

      <div className="relative max-w-6xl mx-auto">
        {/* Eyebrow — connects to Radio family */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-amber-400/90 font-bold">
            <Wrench size={12} className="text-amber-400" />
            A DIVISION OF MINDESIGN ENTERPRISES &amp; JFRJR
            <Wrench size={12} className="text-amber-400" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Logo + label */}
          <div className="flex justify-center order-2 md:order-1">
            <div className="relative max-w-md">
              <div className="absolute inset-0 -m-4 bg-gradient-to-br from-amber-400/20 to-blue-500/10 blur-3xl"></div>
              <img
                src={LOGO_SRC}
                alt="THE AMPED UP LIFE — Podcast & Social Restoration Community"
                className="relative w-full h-auto object-contain rounded-lg shadow-2xl shadow-black/60 border border-amber-400/20"
              />
            </div>
          </div>

          {/* Copy + CTAs */}
          <div className="order-1 md:order-2">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-5">
              <span className="block text-steel-100">THE</span>
              <span
                className="block bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400"
                style={{ textShadow: '0 0 60px rgba(251,191,36,0.3)' }}
              >
                AMPED UP LIFE
              </span>
            </h2>

            <p className="text-amber-200/80 text-base tracking-[0.2em] uppercase font-bold mb-4">
              Podcast · Social Restoration Community
            </p>

            <p className="text-steel-300 text-base leading-relaxed mb-8 max-w-md">
              Real conversations. Real recovery. Real restoration. The MINDESIGN
              podcast where lived experience meets unfiltered truth — interviews,
              testimonies, and the kind of community discussions the daytime feeds
              won't host.
            </p>

            <div className="grid grid-cols-3 gap-4 mb-8 max-w-md">
              <div className="bg-gunmetal-900/60 border border-amber-400/20 rounded-lg p-3 text-center">
                <Mic className="mx-auto text-amber-400 mb-1" size={20} />
                <div className="text-[10px] tracking-widest uppercase text-steel-400">
                  Podcast
                </div>
              </div>
              <div className="bg-gunmetal-900/60 border border-amber-400/20 rounded-lg p-3 text-center">
                <Users className="mx-auto text-amber-400 mb-1" size={20} />
                <div className="text-[10px] tracking-widest uppercase text-steel-400">
                  Community
                </div>
              </div>
              <div className="bg-gunmetal-900/60 border border-amber-400/20 rounded-lg p-3 text-center">
                <Headphones className="mx-auto text-amber-400 mb-1" size={20} />
                <div className="text-[10px] tracking-widest uppercase text-steel-400">
                  Restoration
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-gunmetal-950 font-black tracking-[0.18em] uppercase px-6 py-5 opacity-90 cursor-not-allowed"
                data-testid="amped-up-coming-btn"
              >
                <Mic size={16} className="mr-2" /> Episodes Coming Soon
              </Button>
              <ShareButton
                sectionId="amped-up-life"
                sectionTitle="THE AMPED UP LIFE Podcast"
                description="A MINDESIGN ENTERPRISES & JFRJR podcast — Social Restoration Community. Episodes coming soon."
              />
            </div>

            <p className="text-steel-500 text-xs mt-5 flex items-center gap-1.5">
              <ExternalLink size={11} />
              First episode drop will be announced via the MINDESIGN newsletter.
            </p>
          </div>
        </div>

        {/* YOUR HOSTS — classy portrait feature */}
        <div className="mt-24 pt-12 border-t border-amber-400/20" data-testid="amped-up-hosts">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-amber-400/90 font-bold mb-3">
              <span className="h-px w-10 bg-amber-400/40" />
              WITH YOUR HOSTS
              <span className="h-px w-10 bg-amber-400/40" />
            </div>
            <h3 className="text-3xl sm:text-4xl font-black tracking-tight text-steel-100">
              The Voices Behind The Show
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            {/* Portrait — premium framing */}
            <div className="relative group">
              <div className="absolute inset-0 -m-3 bg-gradient-to-br from-amber-400/30 via-orange-400/20 to-blue-500/20 blur-2xl opacity-70"></div>
              <div className="relative rounded-lg overflow-hidden border-2 border-amber-400/40 shadow-2xl shadow-black/70">
                <img
                  src={HOSTS_SRC}
                  alt="JF KEEPIN IT REAL and LOVE BUG — co-hosts of THE AMPED UP LIFE"
                  className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                />
                {/* Subtle gold corner accent */}
                <div className="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-amber-400/70 pointer-events-none"></div>
                <div className="absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 border-amber-400/70 pointer-events-none"></div>
              </div>
            </div>

            {/* Bios */}
            <div className="space-y-6">
              <div
                className="bg-gunmetal-900/60 border border-amber-400/20 rounded-lg p-5"
                data-testid="host-jf-keepin-it-real"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-400/30">
                    <Mic className="text-gunmetal-950" size={18} />
                  </div>
                  <div>
                    <div className="text-[9px] tracking-[0.3em] uppercase text-amber-400/80 font-bold">
                      Co-Host · Founder
                    </div>
                    <div className="text-xl font-black text-steel-100 tracking-wide">
                      JF KEEPIN IT REAL
                    </div>
                  </div>
                </div>
                <p className="text-steel-300 text-sm leading-relaxed pl-13">
                  Jose Francisco Rodriguez Jr. — author, musician, founder of MINDESIGN
                  ENTERPRISES. Lived experience, raw delivery, zero filter.
                </p>
              </div>

              <div
                className="bg-gunmetal-900/60 border border-amber-400/20 rounded-lg p-5"
                data-testid="host-love-bug"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-amber-400 flex items-center justify-center shadow-lg shadow-pink-400/30">
                    <Heart className="text-gunmetal-950" size={18} />
                  </div>
                  <div>
                    <div className="text-[9px] tracking-[0.3em] uppercase text-amber-400/80 font-bold">
                      Co-Host · Soulmate
                    </div>
                    <div className="text-xl font-black text-steel-100 tracking-wide">
                      LOVE BUG
                    </div>
                  </div>
                </div>
                <p className="text-steel-300 text-sm leading-relaxed pl-13">
                  The one and only. The heart in every conversation, the steady hand
                  beside the storm. Real love, real talk, real recovery — together.
                </p>
              </div>

              <div className="text-center pt-2">
                <p className="text-amber-200/70 text-sm italic tracking-wide">
                  "Two voices. One mission. Restoring lives, one episode at a time."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AmpedUpLife;
